import { useRef, useCallback } from "react";
import { ExportDropdown } from "@components/common/export/export-dropdown";
import { ProjectTableSubject } from "@components/common/faro-table/faro-table-types";
import { ProjectArchivingState, SdbProject } from "@custom-types/project-types";
import { useDateTime } from "@hooks/use-date-time";
import { useExportAllPaginated } from "@hooks/use-export-all-paginated";
import { downloadProjectsData, ExportFormat } from "@utils/file-data-export";
import { TableType } from "@utils/track-event/track-event-list";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import { convertProjectsResponseToSdbProjects } from "@store/projects/projects-slice-utils";
import { projectStateToApiArchivingState } from "@utils/project-utils";
import type { FaroCoreApiTypes } from "@stellar/core-api-client";
import { ExportHandlerOptions, ExportResult } from "@components/common/export/export-dropdown-types";

interface Props {
  /** Company ID */
  companyId: string;

  /** Recognizes on which subject the table is being used */
  subjectType: ProjectTableSubject;

  /** The name of the context type */
  entityName: string;

  /** List of selected groups in table */
  selectedProjects: SdbProject[];

  /** The archiving status of the project */
  projectStatus?: ProjectArchivingState;

  /** True if some entities are selected */
  areEntitiesSelected: boolean;
}

/** Dropdown to export projects in the file format selected by the user */
export function BulkExportProjects({
  companyId,
  subjectType,
  entityName,
  selectedProjects,
  projectStatus,
  areEntitiesSelected,
}: Props): JSX.Element | null {
  const { formatDate } = useDateTime();
  // Cache fetched projects to avoid re-fetching on subsequent exports
  const cachedProjectsRef = useRef<SdbProject[] | null>(null);

  // Generic hook with explicit type parameters
  const { exportAll } = useExportAllPaginated<
    FaroCoreApiTypes.GetProjectsPayload,
    FaroCoreApiTypes.GetProjectsResponse["data"],
    SdbProject
  >();

  /** Fetches all projects with pagination and caches the result */
  const fetchAllProjects = useCallback(
    async (options?: ExportHandlerOptions): Promise<SdbProject[] | null> => {
      // Return cached projects if available
      if (cachedProjectsRef.current) {
        return cachedProjectsRef.current;
      }

      // Fetch all projects with pagination
      const result = await exportAll({
        fetchParams: {
          companyId,
          archivingStates: projectStateToApiArchivingState(
            projectStatus ?? ProjectArchivingState.active
          ),
        },
        fetchPage: async (params) => {
          const response = await faroCoreApiClient.projects.getProjects(params);
          return { data: [response.data], next: response.next };
        },
        transform: (dataArray) => {
          return dataArray.flatMap((responseData) =>
            convertProjectsResponseToSdbProjects(responseData)
          );
        },
        onProgress: options?.onProgress,
        signal: options?.signal,
      });

      // If cancelled, return null
      if (result === null) {
        return null;
      }

      // Cache the result
      cachedProjectsRef.current = result;
      return result;
    },
    [companyId, exportAll, projectStatus]
  );

  /**
   * Unified export handler for both selected and all projects.
   * @returns number of exported entities if succeeded, null if cancelled
   */
  const handleExport = useCallback(
    async (
      format: ExportFormat,
      options?: ExportHandlerOptions
    ): Promise<ExportResult> => {
      const fileName = generateFileName(subjectType, entityName, projectStatus);

      // Determine which projects to export
      const projectsToExport = areEntitiesSelected
        ? selectedProjects
        : await fetchAllProjects(options);

      // If cancelled during fetch, return null
      if (projectsToExport === null) {
        return null;
      }

      // Export the projects
     return  await downloadProjectsData({
        fileName,
        projects: projectsToExport,
        formatDate,
        format,
      });
    },
    [
      areEntitiesSelected,
      entityName,
      formatDate,
      projectStatus,
      selectedProjects,
      subjectType,
      fetchAllProjects,
    ]
  );

  return (
    <ExportDropdown
      trackingProps={{
        dataType: exportedTableType(subjectType, projectStatus),
        numberOfEntities: selectedProjects.length,
        areEntitiesSelected,
      }}
      exportHandler={handleExport}
      toastMessage={{
        success: "Successfully exported projects.",
        error: "Cannot export projects. Please try again.",
      }}
      buttonTooltipText="Export projects"
    />
  );
}

/** Returns the file name */
function generateFileName(
  subjectType: ProjectTableSubject,
  entityName: string,
  projectStatus?: ProjectArchivingState
): string {
  switch (subjectType) {
    case "workspace":
      return `Company ${entityName} - ${projectStatus} project list`;

    case "group":
      return `Group ${entityName} - project list`;
  }
}

/** Returns the exported table type */
function exportedTableType(
  subjectType: ProjectTableSubject,
  projectStatus?: ProjectArchivingState
): TableType {
  switch (subjectType) {
    case "workspace":
      return projectStatus === ProjectArchivingState.active
        ? TableType.activeProjects
        : TableType.archiveProjects;

    case "group":
      return TableType.groupProjects;
  }
}
